import { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { TranslationContext } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { useCollectionsList } from "@is/hooks";
import { CollectionListItem } from "@is/types";

const useManageStoreFavoritesView = () => {
  const { t } = useTranslation(TranslationContext.Settings);
  const {
    collections,
    isLoading,
    addCollection,
    deleteCollection,
    getAllCollectionItems,
    revalidateCollections,
    reorderCollections,
  } = useCollectionsList({ filterOutOfStockItems: false });

  const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [collectionName, setCollectionName] = useState("");
  const [isAllViewOpened, setIsAllViewOpened] = useState(false);

  const [currentCollection, setCurrentCollection] =
    useState<CollectionListItem>(null);

  const {
    settings: { updateViewState: updateSettingsViewState },
  } = useContext(ViewStateContext);

  const handleGoBack = useCallback(() => {
    revalidateCollections();
    updateSettingsViewState({ showStoreFavoritesView: false });
  }, [revalidateCollections, updateSettingsViewState]);

  const handleCollectionModalOpen = useCallback(() => {
    setIsCollectionModalOpen(true);
    setCollectionName("");
  }, []);

  const handleCollectionModalClose = useCallback(() => {
    setIsCollectionModalOpen(false);
    setCollectionName("");
  }, []);

  const handleNameChange = useCallback(
    (value: string) => setCollectionName(value),
    []
  );

  const handleCollectionSave = useCallback(async () => {
    if (!collectionName) {
      return;
    }

    const newCollection = await addCollection({ name: collectionName });

    if (newCollection) {
      setIsCollectionModalOpen(false);
      setCollectionName("");
      setCurrentCollection({
        id: newCollection.id,
        name: newCollection.name,
        thumbnails: [],
        totalItems: 0,
      });
    }
  }, [addCollection, collectionName]);

  const handleCollectionSelect = useCallback(
    (collectionId: string) => {
      const collection = collections?.items.find(
        (item) => item.id === collectionId
      );
      if (collection) {
        setCurrentCollection(collection);
      }
    },
    [collections]
  );

  const handleCurrentCollectionClose = useCallback(() => {
    revalidateCollections();
    setCurrentCollection(null);
  }, [revalidateCollections]);

  const handleRemoveCollectionModalOpen = useCallback(
    () => setIsRemoveModalOpen(true),
    []
  );

  const handleRemoveCollectionModalClose = useCallback(
    () => setIsRemoveModalOpen(false),
    []
  );

  const handleCollectionRemove = useCallback(async () => {
    if (!currentCollection) {
      return;
    }

    await deleteCollection(currentCollection.id);

    revalidateCollections();
    setIsRemoveModalOpen(false);
    setCurrentCollection(null);
  }, [revalidateCollections, currentCollection, deleteCollection]);

  const handleAllViewOpen = useCallback(() => setIsAllViewOpened(true), []);

  const handleAllViewClose = useCallback(() => setIsAllViewOpened(false), []);

  const handleCollectionsReorder = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id || !collections?.items) {
        return;
      }

      // Brand-wide collections are positioned from the admin dashboard, so they are neither
      // draggable nor part of the reorder payload — the stores service rejects a payload that
      // contains one.
      const sortableItems = collections.items.filter(
        (item) => !!item.id && !item.isBrandWide
      );
      const activeIndex = sortableItems.findIndex(
        (item) => item.id === active.id
      );
      const overIndex = sortableItems.findIndex((item) => item.id === over.id);

      if (activeIndex === -1 || overIndex === -1) {
        return;
      }

      const reorderedItems = arrayMove(sortableItems, activeIndex, overIndex);
      const orderedIds = reorderedItems.map((item) => item.id);

      reorderCollections(orderedIds);
    },
    [collections, reorderCollections]
  );

  return {
    t,
    isListOfCollectionsView: !currentCollection && !isAllViewOpened,
    isCollectionEditView: !!currentCollection,
    collections,
    isAllViewOpened,
    isCollectionModalOpen,
    isRemoveModalOpen,
    collectionName,
    currentCollection,
    isLoading,
    handleGoBack,
    handleCollectionModalOpen,
    handleCollectionModalClose,
    handleNameChange,
    handleCollectionSave,
    handleCollectionSelect,
    handleCurrentCollectionClose,
    handleRemoveCollectionModalOpen,
    handleRemoveCollectionModalClose,
    handleCollectionRemove,
    handleAllViewOpen,
    handleAllViewClose,
    getAllCollectionItems,
    handleCollectionsReorder,
  };
};

export default useManageStoreFavoritesView;
